<script setup>
import { AppModelsProgrammingSessionCancellationPolicyType as CancellationPolicyType } from "@classes-service-types/app-models-programming-session-cancellation-policy-type.ts";
import { useVuelidate } from "@vuelidate/core";
import { helpers, maxValue, minValue, numeric, requiredIf } from "@vuelidate/validators";
import { useVModel } from "@vueuse/core";
import { computed, onBeforeMount, unref } from "vue";
import { debounceSearchWrapper, useGetEntities } from "@/components/program-manager/sessions/composable";
import {
  channelTypes,
  entityTypes,
  REGISTRATION_TYPE,
  sessionStatus,
} from "@/components/program-manager/sessions/constants.js";
import { checkIfProgramSessionTypeIsInternshipOnly } from "@/components/program-manager/sessions/utils.js";
import Magnifier from "@/components/shared/icons/Magnifier";
import BasicCheckbox from "@/components/shared/input/BasicCheckbox.vue";
import BasicTextInput from "@/components/shared/input/BasicTextInput.vue";
import ExpandingSection from "@/components/shared/section/ExpandingSection.vue";
import CheckboxSelect from "@/components/shared/select/CheckboxSelect.vue";
import { hasConfigurableRegistrationEnabled, useCustomPriceFees } from "@/composables/authorization.js";
import ApplicationRequirementsFeesPayments from "./ApplicationRequirementsFeesPayments/index.vue";
import ConfigurableApplication from "./ConfigurableApplication.vue";
import StandardApplication from "./StandardApplication.vue";

defineOptions({
  name: "ParticipantSection",
});

const props = defineProps({
  modelValue: {
    type: Object,
  },
  canEditConfigurableFees: {
    type: Boolean,
    default: true,
  },
  ownerEntityId: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(["update:modelValue"]);
const programSession = useVModel(props, "modelValue", emit);

const hasProgramSessionTypeInternshipOnly = computed(() =>
  checkIfProgramSessionTypeIsInternshipOnly(unref(programSession)),
);

// Standard (non-configurable) sessions are always applications; configurable
// sessions only require one when requires_application is set. GPA/age/language
// validations apply only to application sessions.
const requiresApplication = computed(() => {
  if (!hasConfigurableRegistrationEnabled.value) return true;
  if (!programSession.value) return true;
  return programSession.value.requires_application === true;
});

const requiredToEnroll = helpers.withMessage(
  "This field is required for Open Enrollment",
  requiredIf(() => programSession?.value?.status_id === sessionStatus.openEnrollment),
);

const requiredToEnrollGPA = helpers.withMessage(
  "This field is required for Open Enrollment",
  requiredIf(
    () =>
      requiresApplication.value &&
      ((!hasProgramSessionTypeInternshipOnly.value &&
        programSession.value?.is_gpa_required &&
        programSession.value?.status_id === sessionStatus.openEnrollment &&
        (programSession.value.channel_id === channelTypes.CORE ||
          programSession.value.channel_id === `${channelTypes.CORE}`) &&
        programSession.value?.registration_type !== REGISTRATION_TYPE.externalListing) ||
        (programSession.value?.is_gpa_required &&
          programSession.value?.status_id === sessionStatus.openEnrollment &&
          programSession.value?.registration_type !== REGISTRATION_TYPE.externalListing)),
  ),
);

const requiredToEnrollExternalLink = helpers.withMessage(
  "This field is required for Open Enrollment",
  requiredIf(
    () =>
      programSession.value?.is_external_link_required &&
      programSession.value?.registration_type !== REGISTRATION_TYPE.externalListing &&
      programSession?.value?.status_id === sessionStatus.openEnrollment,
  ),
);

const requiredToEnrollExternalLinkOrExternalListing = helpers.withMessage(
  "This field is required for Open Enrollment",
  requiredIf(
    () =>
      programSession.value?.is_external_link_required &&
      programSession?.value?.status_id === sessionStatus.openEnrollment,
  ),
);

const flexibleUrl = helpers.withMessage(
  "URL format is not valid. Please include http:// or https:// in the URL.",
  (value) => {
    if (!value) return true;
    const regex = /^https?:\/\/(?:[a-z0-9-]+\.)+[a-z]{2,}(?::\d+)?(?:[/?#][^\s]*)?$/i;
    return regex.test(value);
  },
);

const rules = {
  allowed_participants: {
    // TODO: No clue what were doing with the validation on this field so leaving this here in case it comes back
    // required: helpers.withMessage(
    //   "Institutions are Required",
    //   requiredIf(function () {
    //     return !programSession?.value?.allowed_participants?.length;
    //   })
    // ),
  },
  forecasted_enrollments: {
    required: requiredToEnroll,
    numeric: helpers.withMessage("Forecasted Enrollments must be a Number", numeric),
  },
  min_participants: {
    required: requiredToEnroll,
    numeric: helpers.withMessage("Minimum Participants must be a Number", numeric),
    minValue: helpers.withMessage("Minimum Participants must be greater than 0", minValue(0)),
  },
  max_participants: {
    numeric: helpers.withMessage("Maximum Participants must be a Number", numeric),
  },
  minimum_age: {
    numeric: helpers.withMessage("Minimum Age must be a Number", numeric),
    minValue: helpers.withMessage("Minimum Age must be greater than 0", minValue(1)),
    maxValue: helpers.withMessage("Minimum Age must be less than 100", maxValue(99)),
  },
  maximum_age: {
    numeric: helpers.withMessage("Maximum Age must be a Number", numeric),
    minValue: helpers.withMessage("Maximum Age must be greater than 0", minValue(1)),
    maxValue: helpers.withMessage("Maximum Age must be less than 100", maxValue(99)),
  },
  education_levels: {}, // Missing link in model
  acceptance_decisions: {},
  gpa_exceptions_allowed: { required: requiredToEnrollGPA },
  gpa_exception_requirements: {
    required: helpers.withMessage(
      "This field is required for Open Enrollment",
      requiredIf(
        () =>
          requiresApplication.value &&
          hasConfigurableRegistrationEnabled.value &&
          ((programSession?.value?.gpa_exceptions_allowed &&
            !hasProgramSessionTypeInternshipOnly.value &&
            programSession.value?.is_gpa_required &&
            programSession?.value?.status_id === sessionStatus.openEnrollment &&
            (programSession.value.channel_id === channelTypes.CORE ||
              programSession.value.channel_id === `${channelTypes.CORE}`) &&
            programSession.value?.registration_type !== REGISTRATION_TYPE.externalListing) ||
            (programSession.value?.is_gpa_required &&
              programSession?.value?.gpa_exceptions_allowed &&
              programSession?.value?.status_id === sessionStatus.openEnrollment &&
              programSession.value?.registration_type !== REGISTRATION_TYPE.externalListing)),
      ),
    ),
  },
  minimum_gpa_requirement: { required: requiredToEnrollGPA },
  cip_code_focuses: {}, // Missing
  academic_level_id: {},
  education_level_minimum_age_rule: {},

  required_languages: {},
  min_profeciency_level: {}, // Missing Link in model
  difficulty_score: {},
  prerequisites: {},
  corequisities: {},
  min_proficiency_level_id: {},
  guest_allowed: {},
  faculty_allowed: {},
  cancellation_policy_type: {},
  cancellation_policy_url: {
    required: helpers.withMessage(
      "A valid URL is required for a session-specific cancellation policy",
      requiredIf(() => programSession.value?.cancellation_policy_type === CancellationPolicyType.Session),
    ),
    flexibleUrl,
  },
  registration_type: {},
  is_personalized: {},
  application_deadline: {
    required: helpers.withMessage(
      "This field is required for Open Enrollment",
      requiredIf(
        () =>
          programSession?.value?.status_id === sessionStatus.openEnrollment &&
          programSession.value?.requires_application,
      ),
    ),
  },
  requires_application: {},
  external_link_site_name: { required: requiredToEnrollExternalLink },
  external_link_instructions: { required: requiredToEnrollExternalLink },
  external_link_url: { required: requiredToEnrollExternalLinkOrExternalListing, flexibleUrl },
};

const {
  execute: executeGetEntities,
  state: entitiesOptions,
  isLoading: entitiesLoading,
} = useGetEntities(
  { immediate: true, throwError: false, resetOnExecute: true },
  {
    extraParams: {
      account_types: [entityTypes.home_institution, entityTypes.host_institution],
    },
  },
);

const fetchEntitiesOptions = debounceSearchWrapper(executeGetEntities, 250, {
  extraParams: {
    account_types: [entityTypes.home_institution, entityTypes.host_institution],
  },
});

let v$ = {};
onBeforeMount(() => {
  // FIXME: If vuelidate fixes https://github.com/vuelidate/vuelidate/issues/764 looks like its in maintained mode so seems unlikely
  v$ = useVuelidate(rules, programSession, {
    $registerAs: "ParticipantsSection",
    $lazy: true,
  });
});
</script>

<template>
  <div class="grid grid-cols-1 gap-y-4">
    <ExpandingSection field-label="PARTICIPATION">
      <template #content>
        <div class="grid grid-cols-2 gap-x-24 gap-y-4">
          <div class="grid grid-cols-2">
            <BasicCheckbox
              v-model="programSession.guest_allowed"
              class="my-auto"
              :vuelidate-instance="v$.guest_allowed"
              field-label="Guest Allowed"
            />
            <BasicCheckbox
              v-model="programSession.faculty_allowed"
              class="my-auto"
              :vuelidate-instance="v$.faculty_allowed"
              field-label="Faculty Allowed"
            />
          </div>
          <CheckboxSelect
            v-model="programSession.allowed_participants"
            tooltip="The participant's home institution must match one of the institutions added here in order to apply to and attend this session."
            :options="entitiesOptions?.items"
            :loading="entitiesLoading"
            :vuelidate-instance="v$.allowed_participants"
            placeholder="Search and add institutions"
            label="name"
            :multiple="true"
            :close-on-select="false"
            :components="{ OpenIndicator: Magnifier }"
            :deselect-from-dropdown="true"
            :clear-search-on-blur="() => false"
            field-label="Select Institution(s)"
            @search="fetchEntitiesOptions"
          >
          </CheckboxSelect>
          <BasicTextInput
            v-model="programSession.forecasted_enrollments"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.forecasted_enrollments"
            type="number"
          >
            <template #fieldLabel>
              <label class="common-label-text"><span class="text-red-100 pr-2">**</span>Forecasted Enrollments</label>
            </template>
          </BasicTextInput>
          <BasicTextInput
            v-model="programSession.min_participants"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.min_participants"
            type="number"
          >
            <template #fieldLabel>
              <label class="common-label-text"
                ><span class="text-red-100 pr-2">**</span>Minimum Participants Required</label
              >
            </template>
          </BasicTextInput>
          <BasicTextInput
            v-model="programSession.max_participants"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.max_participants"
            type="number"
            field-label="Maximum Participants"
          />
        </div>
      </template>
    </ExpandingSection>
    <ExpandingSection
      field-label="Application Requirements &amp; Fees"
      base-classes="border border-gray-300 rounded-sm px-8 py-4 bg-blue-300"
    >
      <template #content>
        <ConfigurableApplication
          v-if="hasConfigurableRegistrationEnabled"
          v-model="programSession"
          :rules="rules"
          :can-edit-configurable-fees="canEditConfigurableFees"
          :owner-entity-id="ownerEntityId"
        />
        <StandardApplication v-else v-model="programSession" :rules="rules" />
        <ApplicationRequirementsFeesPayments
          v-if="useCustomPriceFees && !hasConfigurableRegistrationEnabled"
          v-model="programSession"
          :can-edit-fees="canEditConfigurableFees"
        />
      </template>
    </ExpandingSection>
  </div>
</template>
